import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';
import { User } from '../types/firestore';
import { reportAccessFailure } from '../utils/reportAccessFailure';
import { callWithRetry } from '../utils/callableRetry';
import { shouldClearPreviousDbUser } from '../utils/signupSessionGuards';
import { clearPricingSignupDraft } from '../utils/pricingSignupDraft';

// Auth context types
interface AuthContextType {
  // State
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  userLoading: boolean; // true while DB user is being fetched in background
  userLoadError: string | null;
  emailVerified: boolean;
  error: string | null;
  isAdmin: boolean;

  // Functions
  signUp: (
    email: string,
    userType: 'startup' | 'investor',
    additionalData?: {
      name?: string;
      country?: string;
      hearAboutUs?: string;
      agreeToNewsletter?: boolean;
      agreeToPrivacyPolicy?: boolean;
      promoCode?: string;
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
    }
  ) => Promise<{ userId: string } | void>;
  signIn: (email: string, password: string, stayLoggedIn?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  sendEmailVerification: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Reload Auth only (emailVerified). Does not call getUser. */
  reloadFirebaseUser: () => Promise<void>;
  clearError: () => void;
  clearUserLoadError: () => void;
}

// Helper function to translate Firebase error codes to user-friendly messages
const getFirebaseErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'The email or password you entered is incorrect. Please check your credentials and try again.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many failed login attempts. Please try again later.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'auth/operation-not-allowed':
      return 'Email/password sign in is not enabled. Please contact support.';
    case 'auth/invalid-credential':
      return 'The email or password you entered is incorrect. Please check your credentials and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.';
    case 'auth/requires-recent-login':
      return 'Please log in again to complete this action.';
    default:
      return 'An error occurred during login. Please try again.';
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true); // true until Firebase resolves auth state
  const [userLoading, setUserLoading] = useState(false); // true while fetching DB user
  const [userLoadError, setUserLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const emailVerified = firebaseUser?.emailVerified ?? false;
  const isAdmin = user?.role?.toLowerCase() === 'admin' || (user as any)?.isAdmin === true;

  // Track the uid we last successfully fetched so we don't double-fetch on re-renders
  const fetchedForUid = useRef<string | null>(null);

  // Shared DB user fetcher — used in onAuthStateChanged and refreshUser
  const fetchDbUser = useCallback(async (uid: string) => {
    setUserLoading(true);
    setUserLoadError(null);
    try {
      const getUserFunction = httpsCallable(functions, 'getUser');
      const result = await callWithRetry(() => getUserFunction({ userId: uid }));
      const data = result.data as any;
      if (data?.success && data.user) {
        fetchedForUid.current = uid;
        // Cache userType so ProfileContext can start profile fetch in parallel on the next load
        try {
          localStorage.setItem(`prairi_ut_${uid}`, data.user.userType);
        } catch (_e) {
          // localStorage unavailable (e.g. incognito with strict settings) — degrade gracefully
        }
        setUser(data.user as User);
      } else {
        const message = data?.error || 'Failed to load your account. Please try again.';
        console.error('Failed to load DB user:', message);
        setUser(null);
        setUserLoadError(message);
        reportAccessFailure({
          kind: 'get_user_failed',
          route: window.location.pathname,
          message,
        });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load your account. Please try again.';
      console.error('Error fetching DB user:', err);
      setUser(null);
      setUserLoadError(message);
      reportAccessFailure({
        kind: 'get_user_failed',
        route: window.location.pathname,
        message,
      });
    } finally {
      setUserLoading(false);
    }
  }, []);

  // Firebase auth state — resolves quickly (local cache). DB user fetch starts in the same
  // callback so there's no extra React render cycle before the network request fires.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, fb => {
      setFirebaseUser(fb);
      setLoading(false);
      if (!fb) {
        setUser(null);
        setUserLoadError(null);
        fetchedForUid.current = null;
        return;
      }
      // Persistence is chosen in signIn, before the password call. Setting it again
      // here makes Firebase report a signed-out user for a moment and the login form flashes.
      if (fetchedForUid.current === fb.uid) return;
      // Drop the previous account immediately. Keeping it until getUser returns
      // painted that person's name and profile onto a brand-new signup.
      if (shouldClearPreviousDbUser(fetchedForUid.current, fb.uid)) setUser(null);
      fetchDbUser(fb.uid);
    });
    return unsubscribe;
  }, [fetchDbUser]);

  // When the tab comes back into focus after being backgrounded for hours the browser may
  // have suspended Firebase's token-refresh cycle, leaving the page blank or stuck.
  // Silently force a token refresh on visibility so the session stays alive.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const current = auth.currentUser;
      if (!current) return;
      // getIdToken(true) forces a network refresh. If the token is still valid this
      // is a no-op from the user's perspective. If it was stale, onAuthStateChanged
      // re-fires and the UI updates automatically.
      current.getIdToken(/* forceRefresh */ true).catch(() => {
        // Token refresh failed — usually means the session truly expired.
        // onAuthStateChanged will fire with null and the ProtectedRoute will
        // redirect to login with the current path preserved.
      });
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Sign up function
  const signUp = useCallback(
    async (
      email: string,
      userType: 'startup' | 'investor',
      additionalData?: {
        name?: string;
        country?: string;
        hearAboutUs?: string;
        agreeToNewsletter?: boolean;
        agreeToPrivacyPolicy?: boolean;
        promoCode?: string;
        utmSource?: string;
        utmMedium?: string;
        utmCampaign?: string;
      }
    ) => {
      try {
        setError(null);
        setLoading(true);

        const createUserFunction = httpsCallable(functions, 'createUser');
        const result = await createUserFunction({ email, userType, ...additionalData });
        const data = result.data as any;

        if (!data.success) {
          throw new Error(data.error || 'Failed to create user');
        }

        // console.log('User created successfully:', data.message);
        return { userId: data.userId };
      } catch (error: any) {
        setError(error.message);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Sign in function
  const signIn = useCallback(async (email: string, password: string, stayLoggedIn = true) => {
    try {
      setError(null);
      setLoading(true);

      const persistence = stayLoggedIn ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(auth, persistence);

      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      const friendlyMessage = error.code
        ? getFirebaseErrorMessage(error.code)
        : error.message || 'An error occurred during login. Please try again.';
      setError(friendlyMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign out function - always clear local user so UI is logged out even if Firebase signOut fails
  const signOut = useCallback(async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
    } catch (error: any) {
      setError(error.message);
      // Don't rethrow: still clear local state so user is logged out in the app
    } finally {
      setUser(null);
      clearPricingSignupDraft();
    }
  }, []);

  // Send email verification using Cloud Function (uses custom PRAIRI email template)
  const sendEmailVerification = useCallback(async () => {
    if (!auth.currentUser) throw new Error('No user logged in');

    try {
      setError(null);
      const resendVerificationEmailFn = httpsCallable(functions, 'resendVerificationEmail');
      const result = await resendVerificationEmailFn({});
      const data = result.data as { success: boolean; message?: string };

      if (!data.success) {
        throw new Error(data.message || 'Failed to send verification email');
      }
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (email: string) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  }, []);

  // Reload Firebase user (updates emailVerified) and re-fetch DB user
  const refreshUser = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) return;
    await current.reload();
    setFirebaseUser({ ...current });
    fetchedForUid.current = null;
    await fetchDbUser(current.uid);
  }, [fetchDbUser]);

  const reloadFirebaseUser = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) return;
    await current.reload();
    const next = auth.currentUser;
    setFirebaseUser(next ? { ...next } : null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearUserLoadError = useCallback(() => {
    setUserLoadError(null);
  }, []);

  // Memoized context value — prevents cascading re-renders on every auth state object change
  const value = useMemo<AuthContextType>(
    () => ({
      user,
      firebaseUser,
      loading,
      userLoading,
      userLoadError,
      emailVerified,
      error,
      isAdmin,
      signUp,
      signIn,
      signOut,
      sendEmailVerification,
      resetPassword,
      refreshUser,
      reloadFirebaseUser,
      clearError,
      clearUserLoadError,
    }),
    [
      user,
      firebaseUser,
      loading,
      userLoading,
      userLoadError,
      emailVerified,
      error,
      isAdmin,
      signUp,
      signIn,
      signOut,
      sendEmailVerification,
      resetPassword,
      refreshUser,
      reloadFirebaseUser,
      clearError,
      clearUserLoadError,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
